import * as React from 'react';
import { omit } from 'lodash';
import AdminUpgradeStatsContainer from 'admin/components/AdminUpgradeStats';
import AdminUpgradeFeatures from 'admin/components/AdminUpgradeFeatures/AdminUpgradeFeatures';
import assertions from 'lib/assertions';
import Button from 'components/Button';
import Columns2 from 'components/Columns2';
import { conditionallyAddPlus, trimWhitespaceOnStrings } from 'utils/selfServeHelpers';
import deepMap from 'deep-map';
// @ts-ignore
import Countries, { regionsForCountry, stateIsValid, countryIsValid } from 'lib/geo/Country';
import EventTracking from 'lib/EventTracking';
import FormElement from 'components/FormElement';
import Heading from 'components/Heading';
import Input from 'components/Input';
// @ts-ignore
import InputValueConstants from 'constants/InputValueConstants';
import Link from 'components/Link';
import Notice from 'components/Notice';
import OrganizationPickerContainer from 'components/OrganizationPicker/OrganizationPickerContainer';
import { OrganizationType, QuoteInput } from 'globalTypes';
import PhoneNumberMaskedInput from 'components/PhoneNumberMaskedInput';
// @ts-ignore
import { REQUESTER_ROLE_OPTIONS } from 'admin/constants/AdminUpgrade';
import Row from 'components/Row';
import Select from 'components/Select';
import Text from 'components/Text';
import { asterisked } from 'components/Label';
import ToastActions from 'actions/ToastActions';
import {
  UpgradePlanData,
  UpgradePlanQuote,
} from 'admin/components/AdminUpgradePlan/AdminUpgradePlanContainer';
import { createSelfServeQuote } from 'admin/actions/AdminUpgradePlanActions';
import { baseTrackingPrefix } from 'constants/SelfServeConstants';

import './styles/AdminUpgradePlanOrganizationInfoForm.scss';

type UpdatableOrganizationStateFields = Pick<
  State,
  | 'country'
  | 'postalCode'
  | 'state'
  | 'city'
  | 'streetAddress'
  | 'lat'
  | 'long'
  | 'orgType'
  | 'orgUuid'
  | 'showAddressForm'
>;

type RequiredFormFields = Pick<
  State,
  | 'orgUuid'
  | 'teachersCount'
  | 'studentsCount'
  | 'comment'
  | 'requesterFirstName'
  | 'requesterLastName'
  | 'role'
  | 'phoneNumber'
  | 'requesterEmail'
>;

export type Props = {
  onPersistState: (fields: UpgradePlanData, quote: UpgradePlanQuote, onSuccess: Function) => void;
  onAdvance: () => void;
  onNoOrgStats: () => void;
  onSelectSchool?: (uuid: String) => void;
  initialOrgUuid?: string;
  displayOrgStats?: boolean;
  orgTypeString?: string;
};

type State = {
  country?: string;
  state?: string;
  postalCode?: string;
  city?: string;
  streetAddress?: string;
  orgUuid?: string;
  orgType?: OrganizationType;
  lat?: number;
  long?: number;
  teachersCount?: number;
  studentsCount?: number;
  comment?: string;
  requesterFirstName?: string;
  requesterLastName?: string;
  role?: string;
  phoneNumber?: string;
  requesterEmail?: string;
  showAddressForm: boolean;
  errors: { [key: string]: string };
};

class AdminUpgradePlanOrganizationInfoForm extends React.Component<Props, State> {
  readonly state: State;
  private readonly trackingPrefix = `${baseTrackingPrefix}.organization_info_page`;
  private readonly onscreenFields: (keyof RequiredFormFields)[] = [
    'orgUuid',
    'teachersCount',
    'studentsCount',
    'comment',
    'requesterFirstName',
    'requesterLastName',
    'role',
    'phoneNumber',
    'requesterEmail',
  ];

  constructor(props: Props) {
    super(props);
    this.state = {
      orgUuid: props.initialOrgUuid,
      showAddressForm: false,
      errors: {},
    };
  }

  componentDidMount() {
    this.track('view');
  }

  track = (key: string) => {
    const organization_uuid = this.state.orgUuid || null;
    EventTracking.trackKey(`${this.trackingPrefix}.${key}`, {
      organization_uuid,
    });
  };

  canSubmit = () => {
    return this.onscreenFields.map(k => this.state[k]).every(v => v !== undefined);
  };

  onSubmit = async () => {
    this.track('submit_button.click');
    this.canSubmit() && this.submit();
  };

  submit = async () => {
    const { onPersistState, onAdvance } = this.props;

    const inputs = this.createSelfServeQuoteInputs();
    const response = await createSelfServeQuote(inputs);

    if (response.success) {
      const { showAddressForm, errors, ...upgradePlanData } = this.state;
      onPersistState(upgradePlanData as UpgradePlanData, response.quote, onAdvance);
    } else {
      this.setState({ errors: response.fieldErrors }, () => {
        ToastActions.showErrorToast(response.errorMessage);
      });
    }
  };

  createSelfServeQuoteInputs = (): QuoteInput => {
    const {
      orgUuid,
      studentsCount,
      teachersCount,
      requesterFirstName,
      requesterLastName,
      requesterEmail,
      phoneNumber,
      role,
      lat,
      long,
      streetAddress,
      city,
      state,
      postalCode,
      country,
    } = this.state;

    // Automatically set the start date to one day after the day the
    // org info form is filled out and submitted
    const startDate = new Date();
    startDate.setDate(startDate.getDate() + 1);

    return deepMap(
      {
        organizationUuid: orgUuid,
        studentsCount: studentsCount,
        teachersCount: teachersCount,
        startDate: startDate,
        organizationAddress: {
          latitude: lat || null,
          longitude: long || null,
          streetAddress: streetAddress,
          city: city,
          state: state,
          country: country,
          postalCode: postalCode,
        },
        requesterFirstName: requesterFirstName,
        requesterLastName: requesterLastName,
        requesterEmail: requesterEmail,
        requesterPhoneNumber: phoneNumber ? conditionallyAddPlus(phoneNumber) : undefined,
        requesterRole: role,
        billingFirstName: requesterFirstName,
        billingLastName: requesterLastName,
        billingEmail: requesterEmail,
        billingPhoneNumber: phoneNumber ? conditionallyAddPlus(phoneNumber) : undefined,
        taxExempt: undefined, // Tax exemption status is determined in api
        lastScreenName: `org_info`,
      },
      trimWhitespaceOnStrings,
      { inPlace: true }
    );
  };

  renderSidebar() {
    const { displayOrgStats, initialOrgUuid, onNoOrgStats } = this.props;

    return displayOrgStats && initialOrgUuid ? (
      <AdminUpgradeStatsContainer
        organizationUuid={initialOrgUuid}
        onNoStats={onNoOrgStats}
        data-test="upgrade-stats"
      />
    ) : (
      <AdminUpgradeFeatures data-test="upgrade-features" />
    );
  }

  renderSubmitButton = () => {
    return (
      <Button disabled={!this.canSubmit()} onClick={this.onSubmit} data-test="submit-button">
        Submit
      </Button>
    );
  };

  renderOrganizationPickerForm = () => {
    const { onSelectSchool, orgTypeString } = this.props;
    return (
      <FormElement
        label={asterisked('Organization name')}
        onValidChange={o => {
          this.track('organization_picker.select_organization.click');
          const updates: UpdatableOrganizationStateFields = {
            orgUuid: o.uuid,
            lat: o.latitude,
            long: o.longitude,
            showAddressForm: true,
          };

          if (o.country_code && countryIsValid(o.country_code)) {
            updates.country = o.country_code;
          }
          if (o.state && o.country_code && stateIsValid(o.state, o.country_code)) {
            updates.state = o.state;
          }
          if (o.city) {
            updates.city = o.city;
          }

          if (o.street) {
            updates.streetAddress = o.street;
          }

          if (o.zip) {
            updates.postalCode = o.zip;
          }

          if (o.type) {
            updates.orgType = o.type;
          }

          if (orgTypeString === 'school' && onSelectSchool) {
            onSelectSchool(o.uuid);
          }

          this.setState(updates);
        }}
        element={OrganizationPickerContainer}
        elementProps={{
          onClickInteraction: () => this.track('organization_picker.click'),
          initialOrganizationUuid: this.props.initialOrgUuid,
          onError: () =>
            ToastActions.showErrorToast(
              'There was a problem searching for your Organization. Please try again'
            ),
        }}
        data-test="organization-picker"
      />
    );
  };

  renderAddressForm = () => {
    const regionLabel = regionsForCountry[this.state.country]
      ? regionsForCountry[this.state.country].label
      : 'Region';
    return (
      <div data-test="address-form">
        <FormElement
          label={asterisked('Country')}
          validate={value => {
            assertions.notEmpty(value, 'Please select a country');
          }}
          onValidChange={country => this.setState({ country: country })}
          onInvalidChange={() => this.setState({ country: undefined })}
          element={Select}
          elementProps={{
            name: 'country',
            value: this.state.country,
            options: Countries,
            className: 'AdminUpgradePlanOrganizationInfoForm-countryPicker',
          }}
        />
        <Columns2>
          <FormElement
            label={
              <span>
                {asterisked(
                  this.props.orgTypeString
                    ? `${this.props.orgTypeString} street address`
                    : 'Street address'
                )}{' '}
              </span>
            }
            validate={address => {
              assertions.notEmpty(address);
            }}
            onValidChange={e => this.setState({ streetAddress: e.target.value })}
            onInvalidChange={() => this.setState({ streetAddress: undefined })}
            element={Input}
            elementProps={{
              value: this.state.streetAddress ?? '',
              placeholder: '1234 Remind Road',
              'data-test-selector': 'street-address-input',
            }}
          />
          <FormElement
            label={asterisked('City')}
            validate={city => {
              assertions.notEmpty(city);
            }}
            onValidChange={e => this.setState({ city: e.target.value })}
            onInvalidChange={() => this.setState({ city: undefined })}
            element={Input}
            elementProps={{
              value: this.state.city,
              'data-test-selector': 'city-input',
            }}
          />
        </Columns2>
        <Columns2>
          <FormElement
            label={asterisked(regionLabel)}
            onValidChange={state => this.setState({ state: state })}
            onInvalidChange={() => this.setState({ state: undefined })}
            validate={value => {
              assertions.notEmpty(value, `Please select a ${regionLabel.toLowerCase()}`);
            }}
            element={Select}
            elementProps={{
              name: 'state',
              value: this.state.state,
              options: regionsForCountry[this.state.country]
                ? regionsForCountry[this.state.country].regions
                : [],
              disabled: !this.state.country,
              className: 'AdminUpgradePlanOrganizationInfoForm-regionPicker',
            }}
          />
          <FormElement
            label={asterisked('Postal code')}
            validate={postalCode => {
              assertions.notEmpty(postalCode);
            }}
            onValidChange={e =>
              this.setState({
                postalCode: e.target.value,
                errors: omit(this.state.errors, 'postal_code'),
              })
            }
            onInvalidChange={() => this.setState({ postalCode: undefined })}
            element={Input}
            elementProps={{
              value: this.state.postalCode ?? '',
              'data-test-selector': 'postal-code-input',
            }}
            manualError={this.state.errors['postal_code']}
          />
        </Columns2>
      </div>
    );
  };

  renderAboutOrgForm = () => {
    return (
      <div data-test="about-org-form">
        <Row>
          <Text weight="semibold" size="xlarge" element="div">
            Tell us about your organization
          </Text>
          <Text size="medium" element="div">
            Find or add your school, district, university, or other organization.
          </Text>
        </Row>
        {this.renderOrganizationPickerForm()}
        {this.state.showAddressForm && this.renderAddressForm()}
        <Row size="large">
          <FormElement
            label={<span>{asterisked('Why are you interested in the Remind plan?')}</span>}
            onValidChange={e => this.setState({ comment: e.target.value })}
            onInvalidChange={() => this.setState({ comment: undefined })}
            element={Input}
            elementProps={{
              defaultValue: this.state.comment,
              onFocus: () => this.track('details.click'),
              placeholder:
                "Let us know why you're considering Remind and any questions you might have.",
            }}
          />
        </Row>
        <Columns2>
          <FormElement
            label={asterisked('Estimated teachers on staff')}
            validate={e => {
              assertions.number(e.target.value);
              assertions.positiveNumber(e.target.value);
              assertions.integer(e.target.value);
              assertions.signedInt32(Number(e.target.value));
            }}
            onValidChange={e => this.setState({ teachersCount: e.target.value })}
            onInvalidChange={() => this.setState({ teachersCount: undefined })}
            element={Input}
            elementProps={{
              onFocus: () => this.track('teachers_on_staff.click'),
              defaultValue: this.state.teachersCount,
              'data-test-selector': 'est-teachers-input',
            }}
          />
          <FormElement
            label={asterisked('Estimated student enrollment')}
            validate={e => {
              assertions.number(e.target.value);
              assertions.positiveNumber(e.target.value);
              assertions.integer(e.target.value);
              assertions.signedInt32(Number(e.target.value));
              assertions.valueGreaterThan(5, e.target.value, 'Must include more than five people');
            }}
            onValidChange={e => this.setState({ studentsCount: e.target.value })}
            onInvalidChange={() => this.setState({ studentsCount: undefined })}
            element={Input}
            elementProps={{
              onFocus: () => this.track('student_enrollment.click'),
              defaultValue: this.state.studentsCount,
              'data-test-selector': 'est-students-input',
            }}
          />
        </Columns2>
      </div>
    );
  };

  renderAboutYouSection = () => {
    return (
      <section className="AdminUpgradePlanOrganizationInfoForm-about" data-test="about-you-form">
        <Row size="large">
          <Heading level="2" levelStyle="5">
            <Text size="medium" weight="light" color="eclipse">
              About you
            </Text>
          </Heading>
        </Row>
        <Columns2>
          <FormElement
            label={asterisked('First name')}
            validate={e => {
              assertions.notEmpty(e.target.value);
              assertions.lengthLesserThanOrEqualTo(
                e.target.value,
                InputValueConstants.FIRST_NAME_LENGTH
              );
            }}
            onValidChange={e => this.setState({ requesterFirstName: e.target.value })}
            onInvalidChange={() => this.setState({ requesterFirstName: undefined })}
            element={Input}
            elementProps={{
              onFocus: () => this.track('requester_first_name.click'),
              'data-test-selector': 'first-name-input',
            }}
          />
          <FormElement
            label={asterisked('Last name')}
            validate={e => {
              assertions.notEmpty(e.target.value);
              assertions.lengthLesserThanOrEqualTo(
                e.target.value,
                InputValueConstants.LAST_NAME_LENGTH
              );
            }}
            onValidChange={e => this.setState({ requesterLastName: e.target.value })}
            onInvalidChange={() => this.setState({ requesterLastName: undefined })}
            element={Input}
            elementProps={{
              onFocus: () => this.track('requester_last_name.click'),
              'data-test-selector': 'last-name-input',
            }}
          />
        </Columns2>
        <Columns2>
          <FormElement
            label={asterisked('Role')}
            validate={role => {
              assertions.notEmpty(role);
            }}
            onValidChange={role => {
              this.setState({ role: role });
            }}
            onInvalidChange={() => this.setState({ role: undefined })}
            element={Select}
            elementProps={{
              onFocus: () => this.track('requester_role.click'),
              options: REQUESTER_ROLE_OPTIONS,
              placeholder: 'Select...',
              value: this.state.role,
              className: 'AdminUpgradePlanOrganizationInfoForm-rolePicker',
            }}
          />
        </Columns2>
        <Columns2>
          <FormElement
            label={asterisked('Phone number')}
            validate={e => {
              assertions.number(e.raw, 'Invalid phone number.');
              assertions.completePhoneNumber(e.raw, 'Invalid phone number.');
            }}
            onValidChange={e =>
              this.setState({ phoneNumber: e.raw, errors: omit(this.state.errors, 'phone_number') })
            }
            onInvalidChange={() => this.setState({ phoneNumber: undefined })}
            element={PhoneNumberMaskedInput}
            elementProps={{
              onFocus: () => this.track('requester_phone_number.click'),
              'data-test-selector': 'phone-number-input',
            }}
            manualError={this.state.errors['phone_number']}
          />
          <FormElement
            label={asterisked('Work email address')}
            validate={e => {
              assertions.email(e.target.value);
              assertions.notEmpty(e.target.value);
            }}
            onValidChange={e => {
              this.setState({ requesterEmail: e.target.value });
            }}
            onInvalidChange={() => this.setState({ requesterEmail: undefined })}
            element={Input}
            elementProps={{
              onFocus: () => this.track('requester_email.click'),
              'data-test-selector': 'email-input',
            }}
          />
        </Columns2>
        <Columns2>{this.renderSubmitButton()}</Columns2>
      </section>
    );
  };

  renderLowStudentCountWarning = () => {
    return (
      <Row size="xxxlarge" data-test="student-count-warning">
        <Notice color="polar-bear" noShadow data-test="student-enrollment-too-small">
          <Text size="xsmall">
            Remind plans are only available for schools and districts, but you can always start with
            an individual account! Click <Link to="/">here</Link> to sign up for free.
          </Text>
        </Notice>
      </Row>
    );
  };

  render() {
    return (
      <div className="AdminUpgradePlanOrganizationInfoForm">
        <div className="AdminUpgradePlanOrganizationInfoForm-content">
          <div className="AdminUpgradePlanOrganizationInfoForm-content-body">
            <div className="AdminUpgradePlanOrganizationInfoForm-content-body-forms">
              {this.renderAboutOrgForm()}
              {this.state.studentsCount &&
                this.state.studentsCount <= 5 &&
                this.renderLowStudentCountWarning()}
              {this.renderAboutYouSection()}
            </div>
            <div className="AdminUpgradePlanOrganizationInfoForm-content-body--desktop-side">
              <Row size="xxxlarge">{this.renderSidebar()}</Row>
            </div>
          </div>
        </div>
        <div className="AdminUpgradePlanOrganizationInfoForm--mobile-bottom">
          <div className="AdminUpgradePlanOrganizationInfoForm-info-container">
            {this.renderSidebar()}
          </div>
        </div>
      </div>
    );
  }
}

export { AdminUpgradePlanOrganizationInfoForm };
export default AdminUpgradePlanOrganizationInfoForm;
